import {
  DEFAULT_CHUNK_SIZE,
  uploadLargeFileInChunks,
} from "@/shared/lib/chunk-upload-s3";
import { uploadApi } from "@/shared/lib/upload-api";
import { uploadFileToPresignedUrl } from "@/shared/lib/upload-to-presigned";
import { Button } from "@devhop/ui";
import { Loader2, Upload } from "lucide-react";
import { useCallback, useId, useState } from "react";

/** 5MB – ไฟล์ใหญ่กว่านี้ใช้ chunk upload */
export const DEFAULT_LARGE_FILE_THRESHOLD_BYTES = 5 * 1024 * 1024;

function sanitizeFilename(name: string): string {
  return name.replace(/[^a-zA-Z0-9._-]/g, "_").slice(0, 100) || "file";
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export type SmartFileUploadProps = {
  /** Prefix ของ key ใน bucket เช่น "uploads/demo" */
  keyPrefix?: string;
  /** ไฟล์ใหญ่กว่านี้ (bytes) ใช้ chunk upload, default 5MB */
  largeFileThresholdBytes?: number;
  /** ขนาด chunk เมื่อใช้ chunk upload (bytes), default 5MB */
  chunkSize?: number;
  /** accept attribute ของ input เช่น "image/*" */
  accept?: string;
  /** callback เมื่ออัปโหลดสำเร็จ (ได้ key ใน bucket) */
  onSuccess?: (key: string) => void;
  /** callback เมื่อเกิดข้อผิดพลาด */
  onError?: (error: Error) => void;
  /** ป้ายชื่อปุ่ม */
  buttonLabel?: string;
  /** คำอธิบายสั้น */
  hint?: string;
  className?: string;
};

export function SmartFileUpload({
  keyPrefix = "uploads",
  largeFileThresholdBytes = DEFAULT_LARGE_FILE_THRESHOLD_BYTES,
  chunkSize = DEFAULT_CHUNK_SIZE,
  accept,
  onSuccess,
  onError,
  buttonLabel = "ອັບໂຫຼດໄຟລ์",
  hint,
  className,
}: SmartFileUploadProps) {
  const inputId = useId();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<{ loaded: number; total: number } | null>(null);
  const [lastKey, setLastKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const buildKey = useCallback(
    (file: File): string => {
      const ext = file.name.includes(".")
        ? (file.name.split(".").pop() ?? "")
        : "";
      const baseName = file.name.includes(".")
        ? file.name.slice(0, file.name.lastIndexOf("."))
        : file.name;
      const safeBase = sanitizeFilename(baseName);
      return `${keyPrefix}/${Date.now()}-${safeBase}${ext ? `.${ext}` : ""}`;
    },
    [keyPrefix],
  );

  const handleFile = useCallback(
    async (file: File) => {
      setError(null);
      setLastKey(null);
      setUploading(true);
      setProgress(null);

      const isLarge = file.size > largeFileThresholdBytes;

      try {
        if (isLarge) {
          setProgress({ loaded: 0, total: file.size });
          const key = await uploadLargeFileInChunks(file, {
            keyPrefix,
            chunkSize,
            onProgress: (loaded, total) => setProgress({ loaded, total }),
          });
          setProgress(null);
          setLastKey(key);
          onSuccess?.(key);
        } else {
          const key = buildKey(file);
          const contentType = file.type || undefined;
          const { uploadUrl } = await uploadApi.getPresignUrl({
            key,
            contentType,
          });
          await uploadFileToPresignedUrl(uploadUrl, file, { contentType });
          setLastKey(key);
          onSuccess?.(key);
        }
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err.message);
        setProgress(null);
        onError?.(err);
      } finally {
        setUploading(false);
      }
    },
    [
      keyPrefix,
      largeFileThresholdBytes,
      chunkSize,
      buildKey,
      onSuccess,
      onError,
    ],
  );

  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFile(file);
      e.target.value = "";
    },
    [handleFile],
  );

  const progressPercent =
    progress && progress.total > 0
      ? Math.round((progress.loaded / progress.total) * 100)
      : 0;

  return (
    <div className={`space-y-2 ${className ?? ""}`}>
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="file"
          accept={accept}
          onChange={onInputChange}
          disabled={uploading}
          className="hidden"
          id={inputId}
        />
        <Button
          type="button"
          variant="outline"
          disabled={uploading}
          onClick={() => document.getElementById(inputId)?.click()}
        >
          {uploading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Upload className="h-4 w-4" />
          )}
          <span className="ml-2">
            {uploading
              ? progress
                ? `ກຳລັງອັບໂຫຼດ... ${progressPercent}%`
                : "ກຳລັງອັບໂຫຼດ..."
              : buttonLabel}
          </span>
        </Button>
      </div>

      {progress && progress.total > 0 && (
        <div className="space-y-1">
          <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full bg-primary transition-[width] duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <p className="text-muted-foreground text-xs">
            {formatBytes(progress.loaded)} / {formatBytes(progress.total)}
          </p>
        </div>
      )}

      {hint && <p className="text-muted-foreground text-sm">{hint}</p>}
      {lastKey && (
        <p className="break-all text-muted-foreground text-xs">
          ອັບໂຫຼດແລ້ວ: <code className="rounded bg-muted px-1">{lastKey}</code>
        </p>
      )}
      {error && <p className="text-destructive text-sm">{error}</p>}
    </div>
  );
}
